import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  SecondaryButton,
  PrimaryButton,
} from './StyledComponents';

const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

const DuplicateIconLarge = () => (
  <svg
    width="48"
    height="48"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth="1.5" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
    />
  </svg>
);

const ModalBody = styled.div`
  padding: 0 32px 24px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 24px 20px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};
`;

const Message = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};
`;

const CenteredFooter = styled(ModalFooter)`
  justify-content: center;
`;

const DuplicateConfirmModal = ({
  isOpen,
  onClose,
  title = 'Duplicate Annual Ticket',
  message,
  confirmText = 'Duplicate',
  cancelText = 'Cancel',
  onConfirm,
  isDuplicating = false,
}) => {
  const [modalRoot, setModalRoot] = useState(null);

  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isDuplicating) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isDuplicating, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isDuplicating) {
      onClose();
    }
  };

  if (!isOpen || !modalRoot) return null;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="alertdialog"
          aria-labelledby="duplicate-modal-title"
          aria-describedby="duplicate-modal-message"
          aria-modal="true"
          maxWidth="420px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
            disabled={isDuplicating}
          >
            <CloseIcon />
          </CloseButton>

          <ModalHeader>
            <ModalTitle id="duplicate-modal-title">{title}</ModalTitle>
          </ModalHeader>

          <ModalBody>
            <IconWrapper>
              <DuplicateIconLarge />
            </IconWrapper>
            <Message id="duplicate-modal-message">{message}</Message>
          </ModalBody>

          <CenteredFooter>
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={isDuplicating}
            >
              {cancelText}
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={onConfirm}
              disabled={isDuplicating}
            >
              {isDuplicating ? 'Duplicating...' : confirmText}
            </PrimaryButton>
          </CenteredFooter>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

DuplicateConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  isDuplicating: PropTypes.bool,
};

export default DuplicateConfirmModal;
